import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

/**
 * Fade Up Animation
 * @param {HTMLElement} element - The element to animate
 * @param {number} delay - Delay in seconds
 */
export const animateFadeUp = (element, delay = 0) => {
    if (!element) return;
    gsap.fromTo(
        element,
        { opacity: 0, y: 50 },
        {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: delay,
            ease: "power3.out",
            scrollTrigger: {
                trigger: element,
                start: "top 85%",
                toggleActions: "play none none reverse",
            },
        }
    );
};

/**
 * Fade Left Animation
 * @param {HTMLElement} element - The element to animate
 * @param {number} delay - Delay in seconds
 */
export const animateFadeLeft = (element, delay = 0) => {
    if (!element) return;
    gsap.fromTo(
        element,
        { opacity: 0, x: -50 },
        {
            opacity: 1,
            x: 0,
            duration: 1,
            delay: delay,
            ease: "power3.out",
            scrollTrigger: {
                trigger: element,
                start: "top 85%",
                toggleActions: "play none none reverse",
            },
        }
    );
};

/**
 * Fade Right Animation
 * @param {HTMLElement} element - The element to animate
 * @param {number} delay - Delay in seconds
 */
export const animateFadeRight = (element, delay = 0) => {
    if (!element) return;
    gsap.fromTo(
        element,
        { opacity: 0, x: 50 },
        {
            opacity: 1,
            x: 0,
            duration: 1,
            delay: delay,
            ease: "power3.out",
            scrollTrigger: {
                trigger: element,
                start: "top 85%",
                toggleActions: "play none none reverse",
            },
        }
    );
};

/**
 * Stagger Children Animation
 * @param {HTMLElement[]} elements - Array of elements to animate
 * @param {number} delay - Delay in seconds
 * @param {number} stagger - Stagger time in seconds
 */
export const animateStagger = (elements, delay = 0, stagger = 0.2) => {
    if (!elements || elements.length === 0) return;

    // Ensure elements is an array
    const elArray = Array.isArray(elements) ? elements : Array.from(elements);

    gsap.fromTo(
        elArray,
        { opacity: 0, y: 30 },
        {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: delay,
            stagger: stagger,
            ease: "power3.out",
            scrollTrigger: {
                trigger: elArray[0],
                start: "top 85%",
                toggleActions: "play none none reverse",
            },
        }
    );
};

/**
 * Counter Animation
 * @param {HTMLElement} element - The element containing the number
 * @param {number} endValue - The final number value
 */
export const animateCounter = (element, endValue) => {
    if (!element) return;

    // If endValue is not provided, try to parse it from the element's text
    const finalValue = endValue !== undefined ? endValue : parseInt(element.innerText.replace(/,/g, ''), 10);

    if (isNaN(finalValue)) return;

    gsap.fromTo(element,
        { textContent: 0 },
        {
            textContent: finalValue,
            duration: 2,
            ease: "power1.out",
            snap: { textContent: 1 },
            scrollTrigger: {
                trigger: element,
                start: "top 85%",
                once: true,
            },
            onUpdate: function () {
                element.innerHTML = Math.ceil(this.targets()[0].textContent);
            }
        }
    );
}

/**
 * Page Intro Animation
 * @param {HTMLElement} container - The main container to animate in
 */
export const animatePageIntro = (container) => {
    if (!container) return;
    const tl = gsap.timeline();
    tl.fromTo(container,
        { opacity: 0 },
        { opacity: 1, duration: 1, ease: "power2.out" }
    );
    return tl;
}

/**
 * Parallax Animation
 * @param {HTMLElement} element - The element to animate
 * @param {number} speed - Parallax speed
 */
export const animateParallax = (element, speed = 0.5) => {
    if (!element) return;
    gsap.to(element, {
        y: -100 * speed,
        ease: "none",
        scrollTrigger: {
            trigger: element,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
        }
    });
}

/**
 * Zoom In Animation (for icons/images)
 * @param {HTMLElement} element - The element to animate
 * @param {number} delay - Delay in seconds
 */
export const animateZoomIn = (element, delay = 0) => {
    if (!element) return;
    gsap.fromTo(
        element,
        { opacity: 0, scale: 0.5 },
        {
            opacity: 1,
            scale: 1,
            duration: 0.8,
            delay: delay,
            ease: "back.out(1.7)",
            scrollTrigger: {
                trigger: element,
                start: "top 90%",
                toggleActions: "play none none reverse",
            },
        }
    );
};

/**
 * Image Reveal Animation (Scale & Fade)
 * @param {HTMLElement} element - The element to animate
 * @param {number} delay - Delay in seconds
 */
export const animateImageReveal = (element, delay = 0) => {
    if (!element) return;
    gsap.fromTo(
        element,
        { opacity: 0, scale: 1.1, filter: "blur(10px)" },
        {
            opacity: 1,
            scale: 1,
            filter: "blur(0px)",
            duration: 1.2,
            delay: delay,
            ease: "power2.out",
            scrollTrigger: {
                trigger: element,
                start: "top 85%",
                toggleActions: "play none none reverse",
            },
        }
    );
};
